// Inactivity session rules — BROWSER TABS ONLY. The installed PWA keeps its
// persistent session (field workers must stay signed in for offline work).
// Client-side only: Supabase global JWT/refresh settings are untouched.
// Storage only ever holds non-sensitive timing metadata (timestamps / minute
// values) — never tokens or personal data. Server-side JWT expiry still rules.

import { logWarn } from "@/lib/log";

export const isStandalonePWA = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const DEFAULT_LIMIT_MS = 30 * 60 * 1000; // fallback when role not yet known
const DEFAULT_WARN_MS = 60 * 1000;       // warn 1 min before

export const LAST_ACTIVITY_KEY = "hakina_last_activity";
export const IDLE_SIGNOUT_FLAG = "hakina_idle_signout";
export const IDLE_LIMIT_MIN_KEY = "hakina_idle_limit_min"; // role limit cache (minutes)

// Per-ROLE inactivity limits (minutes) for BROWSER sessions — adjustable defaults.
// The installed PWA never applies these (see isStandalonePWA gate in IdleGuard).
export const ROLE_IDLE_MINUTES = {
  super_admin: 30,
  org_admin: 30,
  supervisor: 60,
  field_employee: 60,
};

// Cache the signed-in user's role-based limit locally so the boot expiry
// check works before memberships load on the next launch.
export const rememberIdleRole = (role) => {
  const minutes = ROLE_IDLE_MINUTES[role] ?? 30;
  try {
    localStorage.setItem(IDLE_LIMIT_MIN_KEY, String(minutes));
  } catch (e) {
    logWarn("idle: could not persist role idle limit", e);
  }
};

const roleLimitMs = () => {
  try {
    const m = Number(localStorage.getItem(IDLE_LIMIT_MIN_KEY));
    return Number.isFinite(m) && m >= 1 && m <= 480 ? m * 60 * 1000 : DEFAULT_LIMIT_MS;
  } catch (e) {
    logWarn("idle: storage unavailable, using default limit", e);
    return DEFAULT_LIMIT_MS;
  }
};

// QA override (shortens only — lets tests demo the flow without waiting 30 min).
const override = (key) => {
  try {
    const v = Number(localStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null; // storage unavailable — no override, safe default
  }
};
export const idleLimitMs = () => Math.min(override("hakina_idle_limit_override_ms") ?? roleLimitMs(), roleLimitMs());
export const idleWarnMs = () => Math.min(override("hakina_idle_warn_override_ms") ?? DEFAULT_WARN_MS, idleLimitMs());

export const readLastActivity = () => {
  try {
    const v = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    if (!Number.isFinite(v) || v <= 0) return null;
    // Tamper/clock-skew guard: a future timestamp is treated as "active now",
    // never as extra idle credit beyond the present moment.
    return Math.min(v, Date.now());
  } catch (e) {
    logWarn("idle: could not read last activity", e);
    return null;
  }
};

export const writeLastActivity = (ts = Date.now()) => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch (e) {
    logWarn("idle: could not persist last activity", e);
  }
};

export const clearLastActivity = () => {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch (e) {
    logWarn("idle: could not clear last activity", e);
  }
};

// STEP 7 EXCEPTION (2026-09-16, narrow by design): a boot URL whose hash fragment
// carries tokens from a just-clicked emailed link IS fresh user activity — the idle
// guard must not execute that session. Included types, explicitly:
//   recovery      — password reset links (the guard was destroying the recovery
//                   session before the form could use it)
//   email_change  — emailed confirm links, same click-to-boot shape
// Deliberately EXCLUDED: magiclink & signup (this app is password + invite-only and
// issues neither; if ever adopted they must be reviewed here), and any fragment
// without BOTH an access_token and a listed type. Invite acceptance does not need
// this: /accept-invite verifies on an isolated client via a ?token_hash= QUERY param —
// no session ever reaches the global client at boot.
const FRESH_AUTH_FRAGMENT_TYPES = ["recovery", "email_change"];
export const hashHasFreshAuthFragment = (hash) => {
  try {
    const h = hash || "";
    if (!h.includes("access_token=")) return false;
    const params = new URLSearchParams(h.replace(/^#/, ""));
    return FRESH_AUTH_FRAGMENT_TYPES.includes(params.get("type") || "");
  } catch {
    return false;
  }
};

// True when a stored last-activity timestamp exists and is past the limit —
// used both on app open (survives browser close) and by the live timer.
export const isIdleExpired = () => {
  const last = readLastActivity();
  return last !== null && Date.now() - last >= idleLimitMs();
};

export const markIdleSignout = () => {
  try {
    sessionStorage.setItem(IDLE_SIGNOUT_FLAG, "1");
  } catch (e) {
    logWarn("idle: could not mark idle signout", e);
  }
};

export const consumeIdleSignoutFlag = () => {
  try {
    const v = sessionStorage.getItem(IDLE_SIGNOUT_FLAG) === "1";
    sessionStorage.removeItem(IDLE_SIGNOUT_FLAG);
    return v;
  } catch {
    return false; // storage unavailable — no banner, harmless
  }
};
